import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Plus, SlidersHorizontal, Upload } from 'lucide-react';
import { Button, InstrumentTag } from '../ds';
import { ClipSidebar } from './ClipSidebar';
import { ClipPanel, DraftClipPanel } from './ClipPanel';
import { AddClipDialog } from './ClipDialogs';
import { VerifyDialog, RenameProjectDialog, DeleteProjectDialog } from './ProjectDialogs';
import { ErrorNote } from './ErrorNote';
import { StudioBar, StudioMark } from '../studio/StudioBar';
import { ProjectMenu } from '../studio/ProjectMenu';
import { useStudioReady } from '../studio/studioEntrance';
import { useAuth } from '../../context/AuthContext';
import { useProject, isDraftId, draftReplacement } from '../../hooks/useProject';
import { useCredits } from '../../hooks/useCredits';

/**
 * /projects/:projectId/:clipId — a project's clips beside the selected clip's chat, under the one
 * studio bar: the project's menu, then from tablet up the open clip with its Edit and Upload.
 * Without a clip in the address it opens the first one.
 */
export const ProjectWorkspace = () => {
  const { projectId, clipId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isVerified = Boolean(user?.is_verified);
  const { creditsRemaining, setCreditsRemaining } = useCredits();
  const { project, clips, loading, error, upsertClip, removeClip, startClip, rename, remove } =
    useProject(projectId, { onCredits: setCreditsRemaining });
  const [adding, setAdding] = useState(false);
  // A new clip whose analysis failed: Add clip opens again with what they chose, and why
  const [retry, setRetry] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [projectDialog, setProjectDialog] = useState(null); // rename, delete

  const openAddClip = useCallback(() => (isVerified ? setAdding(true) : setVerifying(true)), [isVerified]);

  // A new, empty project opens straight into Add clip, once (not again after its last clip is deleted)
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!loading && project && project.clips.length === 0 && isVerified && !autoOpened.current) {
      autoOpened.current = true;
      setAdding(true);
    }
  }, [loading, project, isVerified]);

  // The open clip: a real clip's number, or a new clip's draft id while its first analysis runs
  const selectedId = isDraftId(clipId) ? clipId : Number(clipId) || null;
  const selectedClip = clips.find((clip) => clip.id === selectedId) || null;
  const isDraft = Boolean(selectedClip?.draft);
  const nextVersion = (selectedClip?.latest_version || 0) + 1;
  // The bar's Edit and Upload act on the open clip's panel: its dialog, and its hidden file input
  const [editingClip, setEditingClip] = useState(false);
  const [clipBusy, setClipBusy] = useState(false);
  const uploadInputRef = useRef(null);
  useEffect(() => setEditingClip(false), [selectedId]);

  // The studio loading screen waits for the project, then for the clip it opens (ClipPanel says so)
  useStudioReady(Boolean(error) || (!loading && project && clips.length === 0));

  if (!loading && project) {
    // A draft that's gone: it finished (follow it to the real clip) or failed (back to the project)
    if (isDraftId(selectedId) && !selectedClip) {
      const replacement = draftReplacement(selectedId);
      return <Navigate to={`/projects/${projectId}${replacement ? `/${replacement}` : ''}`} replace />;
    }
    if (!selectedId && clips.length > 0) {
      return <Navigate to={`/projects/${projectId}/${clips[0].id}`} replace />;
    }
  }

  const addClip = (fields) => {
    const { draftId, done } = startClip(fields);
    setAdding(false);
    setRetry(null);
    navigate(`/projects/${projectId}/${draftId}`);
    done.catch((err) => {
      if (err.status === 403) {
        setVerifying(true);
        return;
      }
      setRetry({ fields, error: err.message || 'Something went wrong. Please try again.' });
      setAdding(true);
    });
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-base text-ink">
      <StudioBar
        creditsRemaining={creditsRemaining}
        actions={selectedClip && (
          // From tablet up; on phones the clip's own header has these
          <div className="hidden md:flex items-center gap-space-2 pr-space-2 mr-space-1 border-r border-line">
            <Button variant="icon" icon={SlidersHorizontal} aria-label="Edit clip" onClick={() => setEditingClip(true)} disabled={isDraft} />
            <Button
              variant="icon"
              icon={Upload}
              aria-label={`Upload V${nextVersion}`}
              onClick={() => uploadInputRef.current?.click()}
              disabled={clipBusy || isDraft}
              className="border border-control text-ink lg:hidden"
            />
            <Button variant="secondary" icon={Upload} onClick={() => uploadInputRef.current?.click()} disabled={clipBusy || isDraft} className="hidden lg:inline-flex">
              Upload V{nextVersion}
            </Button>
          </div>
        )}
      >
        <StudioMark />
        {project && (
          <ProjectMenu
            project={project}
            quiet={Boolean(selectedClip)}
            onRename={() => setProjectDialog('rename')}
            onDelete={() => setProjectDialog('delete')}
          />
        )}
        {selectedClip && (
          <div className="hidden md:flex min-w-0 items-center gap-space-2">
            <ChevronRight size={16} strokeWidth={2.5} aria-hidden="true" className="flex-none text-muted" />
            <h1 className="m-0 min-w-0 truncate font-display text-title-3 font-extrabold text-ink">{selectedClip.name}</h1>
            <InstrumentTag category={selectedClip.category} type={selectedClip.instrument_type} className="hidden lg:inline-flex flex-none" />
            {selectedClip.latest_version && (
              <span className="flex-none font-mono text-caption text-muted">V{selectedClip.latest_version}</span>
            )}
          </div>
        )}
      </StudioBar>

      {error ? (
        <div className="p-space-8 max-w-chat-max">
          <ErrorNote>{error}</ErrorNote>
          <Button as={Link} to="/projects" variant="text" className="mt-space-4">Back to projects</Button>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <ClipSidebar
            clips={clips}
            selectedId={selectedId}
            onSelect={(id) => navigate(`/projects/${projectId}/${id}`)}
            onAddClip={openAddClip}
          />
          {isDraft ? (
            <DraftClipPanel key={selectedClip.id} clip={selectedClip} />
          ) : selectedId ? (
            <ClipPanel
              key={selectedId}
              clipId={selectedId}
              isVerified={isVerified}
              onNeedsVerification={() => setVerifying(true)}
              onClipChange={upsertClip}
              editing={editingClip}
              onEdit={setEditingClip}
              uploadInputRef={uploadInputRef}
              onBusyChange={setClipBusy}
              onClipDeleted={(id) => {
                removeClip(id);
                navigate(`/projects/${projectId}`, { replace: true });
              }}
              onCredits={setCreditsRemaining}
            />
          ) : (
            !loading && (
              <div className="flex-1 flex flex-col items-start gap-space-4 p-space-8 font-text">
                <h1 className="m-0 font-display text-title-2 text-ink">Add your first clip</h1>
                <p className="m-0 max-w-[560px] text-body text-ink-soft">
                  Upload a stem, like your kick or lead vocal, or your full mix. You'll get a score, what to fix next and
                  the plugin moves to fix it.
                </p>
                <Button icon={Plus} onClick={openAddClip}>Add clip</Button>
              </div>
            )
          )}
        </div>
      )}

      <AddClipDialog
        open={adding}
        projectName={project?.name}
        retry={retry}
        onClose={() => {
          setAdding(false);
          setRetry(null);
        }}
        onAdd={addClip}
      />
      <VerifyDialog open={verifying} onClose={() => setVerifying(false)} />
      <RenameProjectDialog
        open={projectDialog === 'rename'}
        project={project}
        onClose={() => setProjectDialog(null)}
        onRename={async (name) => {
          await rename(name);
          setProjectDialog(null);
        }}
      />
      <DeleteProjectDialog
        open={projectDialog === 'delete'}
        project={project}
        onClose={() => setProjectDialog(null)}
        onDelete={async () => {
          await remove();
          navigate('/projects', { replace: true });
        }}
      />
    </div>
  );
};
